import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState, type JSX, type ReactNode } from 'react';

import { Toaster } from '@/components/ui/sonner.js';
import { TooltipProvider } from '@/components/ui/tooltip.js';
import { notify } from '@/lib/notify.js';
import { ThemeProvider } from '@/ui/theme/ThemeProvider.js';

/**
 * App-wide providers — single mount point for everything that needs to wrap
 * the React tree before any feature renders.
 *
 * Order matters:
 * 1. ThemeProvider — drives `data-theme` attrs that the rest of the tree reads.
 * 2. QueryClientProvider — server state, must wrap any feature using TanStack Query.
 * 3. TooltipProvider — single shared instance so tooltip delays coalesce.
 * 4. Toaster — sonner portal at the root.
 */
export function AppProviders({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  // Single QueryClient per app instance. Memoised via lazy useState so React
  // strict-mode double-render doesn't create two clients on first paint.
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            gcTime: 5 * 60_000,
            refetchOnWindowFocus: false,
            retry: 1,
          },
          mutations: {
            retry: 0,
          },
        },
      }),
  );

  // Bridge legacy `ct-attachment-error` window events from the integration
  // attachment adapter into sonner so we have a single toast portal in the
  // app. Removed in Phase 5 once the assistant-ui runtime owns attachment
  // errors directly.
  useEffect(() => {
    function handler(e: Event): void {
      const detail = (e as CustomEvent<string>).detail;
      if (typeof detail === 'string' && detail.length > 0) {
        notify.error(detail);
      }
    }
    window.addEventListener('ct-attachment-error', handler);
    return () => window.removeEventListener('ct-attachment-error', handler);
  }, []);

  // Cast required: TS 5.9 + @types/react@18 bigint/ReactNode mismatch — runtime is correct.
  const nodes = children as JSX.Element;
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          {nodes}
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
